import { getStroke } from 'perfect-freehand'
import type { InkAnn, LineAnn } from '@/types'

/** Smooth, pressure-aware outline polygon for an ink stroke, as an SVG path (base space). */
export function inkPath(a: Pick<InkAnn, 'points' | 'width' | 'marker'>, live = false): string {
  const hasRealPressure = a.points.some(([, , p]) => p !== 0.5 && p > 0)
  const outline = getStroke(a.points, {
    size: a.width,
    thinning: a.marker ? 0 : hasRealPressure ? 0.6 : 0.5,
    smoothing: 0.6,
    streamline: a.marker ? 0.6 : 0.45,
    simulatePressure: !a.marker && !hasRealPressure,
    start: { cap: true, taper: 0 },
    end: { cap: true, taper: 0 },
    last: !live,
  })
  return polygonToPath(outline)
}

function polygonToPath(pts: number[][]): string {
  if (!pts.length) return ''
  if (pts.length < 3) {
    const [x, y] = pts[0]
    return `M ${x} ${y} Z`
  }
  // Quadratic smoothing through midpoints keeps the path compact and smooth.
  const f = (n: number) => Math.round(n * 100) / 100
  let d = `M ${f(pts[0][0])} ${f(pts[0][1])} Q`
  for (let i = 0; i < pts.length; i++) {
    const [x0, y0] = pts[i]
    const [x1, y1] = pts[(i + 1) % pts.length]
    d += ` ${f(x0)} ${f(y0)} ${f((x0 + x1) / 2)} ${f((y0 + y1) / 2)}`
  }
  return `${d} Z`
}

/** Triangle for an arrow head at (x2, y2). */
export function arrowHead(a: Pick<LineAnn, 'x1' | 'y1' | 'x2' | 'y2' | 'strokeWidth'>) {
  const len = Math.max(8, a.strokeWidth * 4)
  const ang = Math.atan2(a.y2 - a.y1, a.x2 - a.x1)
  const spread = Math.PI / 7
  const p1 = { x: a.x2 - len * Math.cos(ang - spread), y: a.y2 - len * Math.sin(ang - spread) }
  const p2 = { x: a.x2 - len * Math.cos(ang + spread), y: a.y2 - len * Math.sin(ang + spread) }
  // shorten the shaft so the line cap doesn't poke through the tip
  const back = len * Math.cos(spread) * 0.8
  const shaftEnd = { x: a.x2 - back * Math.cos(ang), y: a.y2 - back * Math.sin(ang) }
  return {
    path: `M ${a.x2} ${a.y2} L ${p1.x} ${p1.y} L ${p2.x} ${p2.y} Z`,
    tip: { x: a.x2, y: a.y2 },
    p1,
    p2,
    shaftEnd,
  }
}
